import React, { MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { IconCalendar, IconChevronLeft, IconChevronRight, IconCoins, IconDiscount2 } from "@tabler/icons-react";
import { useFloating, useHover, useInteractions } from "@floating-ui/react";
import {
  AnyPeriod,
  castDate,
  classNames,
  debounce,
  GQLPeriod,
  momentizePeriod,
  toGQLPeriod,
  useIsMounted
} from "@ct-react/core";
import { LocaleLink, useLocaleContext, useLocaleFormatter, useLocaleNavigate } from "@ct-react/locale";
import { isDiscountedPrice } from "@ct-react/calendar";
import { buildArticleUrl } from "@shared/urls";
import { bookingTranslations } from "../../i18n/sharable-defs";
import { DataProps } from "../../tools/components";
import {
  AnyArticleSummary,
  Discount,
  isAnnualArticleSummary,
  isRentalArticleSummary,
  isSaleArticleSummary,
  isSeasonalArticleSummary
} from "../../models/article";
import { Ratio } from "../../models/medias";
import RatioImage from "../common/ratio-image";
import ResumeFeatures from "./resume-features";
import "./summary.scss";
import TagList from "../common/tag-list";

type SummaryProps = DataProps<AnyArticleSummary> & {
  view?: "card" | "map";
  ratio?: Ratio;
  fixedDiscountView?: boolean;
}

type DisplayDiscountProps = DataProps<Discount[]> & {
  fixed?: boolean;
  onClick: (period: AnyPeriod) => void;
}

const DisplayDiscount = (
  {
    data,
    fixed = false,
    onClick
  }: DisplayDiscountProps) => {

  const intl = useIntl();
  const {locale} = useLocaleContext();
  const {print} = useLocaleFormatter();

  const [visible, setVisible] = useState(fixed);

  // floating

  const {refs, context} = useFloating({
    open: visible,
    ...(!fixed) && {onOpenChange: setVisible}
  });

  const {getReferenceProps, getFloatingProps} = useInteractions([
    useHover(context, {move: false})
  ]);

  const priceIntlValues = useCallback((discount: Discount) => ({
    val: (
      <span className="value">
        {discount.type === "FIXED" ? print.price(discount.fixedValue) : discount.percentageValue * 100 + "%"}
      </span>
    )
  }), [locale])

  const periodIntlValue = useCallback((period: AnyPeriod) => {
    const val = momentizePeriod(period);
    const isSameMonth = val.start.isSame(val.end, "month");
    return {
      start: intl.formatDate(val.start.toDate(), {day: "numeric", ...(!isSameMonth) && {month: "short"}}),
      end: intl.formatDate(val.end.toDate(), {day: "numeric", month: "short"})
    };
  }, [locale]);

  // dom interactions

  const blockEvent = (e: MouseEvent<HTMLDivElement>) => e.preventDefault();

  const onDiscountClick = (discount: Discount, e: MouseEvent<HTMLDivElement>) => {
    if (discount.logic === "FIXED") {
      e.preventDefault();
      onClick(discount.period);
    }
  }

  // rendering

  const wrapperClasses = classNames("discount-wrapper", {visible});

  return (
    <div ref={refs.setReference}
         {...getReferenceProps()}
         className={wrapperClasses}>
      <div className="discount-box"
           onClick={blockEvent}><IconDiscount2/></div>
      {visible &&
        <div ref={refs.setFloating}
             {...getFloatingProps()}
             className="discounts-list">
          {data.slice(0, 3).map((discount, i) => (
            <div key={i}
                 className="one-discount"
                 onClick={onDiscountClick.bind(this, discount)}>
              <div className="value">
                {intl.formatMessage(bookingTranslations.discount, priceIntlValues(discount))}
              </div>
              <div className="period">
                {discount.logic === "FIXED"
                  ? <><IconCalendar/>{print.period(discount.period)}</>
                  : <span>
                    {intl.formatMessage(bookingTranslations.discountPeriod, periodIntlValue(discount.period))}
                </span>
                }
              </div>
              {discount.logic === "FIXED" && <div className="targetable"><IconChevronRight/></div>
              }
            </div>)
          )}
        </div>
      }
    </div>);

};

const Summary = (
  {
    data,
    view = "card",
    ratio = Ratio.FULL_CARD,
    fixedDiscountView = false
  }: SummaryProps) => {

  const intl = useIntl();
  const isMounted = useIsMounted();
  const navigate = useLocaleNavigate();
  const {print} = useLocaleFormatter();

  const isMapView = useMemo(() => view === "map", [view]);


  // component refs

  const sliderRef = useRef<HTMLDivElement>(null);
  const sliderTrackRef = useRef<HTMLDivElement>(null);
  const bulletsRef = useRef<HTMLDivElement>(null);
  const bulletTrackRef = useRef<HTMLUListElement>(null);
  const bulletRef = useRef<HTMLLIElement>(null);


  // component states

  const [scrollIndex, setScrollIndex] = useState(0);
  const [scrollLength, setScrollLength] = useState(data.images?.length ?? 1);

  useEffect(() => {
    if (!isMounted) return;
    setScrollIndex(0);
    setScrollLength(data.images?.length ?? 1);
  }, [data.images]);

  useEffect(() => {
    if (!isMounted) return;
    bulletTrackRef.current!.scroll({
      behavior: "smooth",
      left: Math.max(scrollIndex - 2) * bulletRef.current!.scrollWidth
    })
  }, [scrollIndex]);

  const target = useMemo(() => {
    const url = buildArticleUrl(data);
    if (!isRentalArticleSummary(data) || !data.bookingSuggestion)
      return url;
    const params = toGQLPeriod(data.bookingSuggestion.period);
    return `${url}&checkin=${params[0]}&checkout=${params[1]}`;
  }, [data.id, isRentalArticleSummary(data) ? data.bookingSuggestion : undefined]);


  // dom interactions

  const onDiscountClick = (period: AnyPeriod) => {
    const params = toGQLPeriod(period);
    navigate(buildArticleUrl(data) + `&checkin=${params[0]}&checkout=${params[1]}`);
  }

  const onSliderScroll = debounce(() => {
    const newIndex = Math.round(sliderTrackRef.current!.scrollLeft / sliderRef.current!.scrollWidth);
    setScrollIndex(newIndex);
  }, 125);

  const onSliderNav = (e: MouseEvent, direction: -1 | 1) => {
    e.preventDefault();
    if ((direction === -1 && scrollIndex === 0) || (direction === 1 && scrollIndex + 1 === scrollLength))
      return;
    sliderTrackRef.current!.scroll({
      behavior: "smooth",
      left: sliderTrackRef.current!.scrollLeft + (direction * sliderRef.current!.scrollWidth)
    });
  }


  // rendering

  const wrapperClasses = classNames("rn-card", {"map-view": isMapView});
  const suggestionClasses = classNames("book-date", {
    gridded: isSeasonalArticleSummary(data) || isAnnualArticleSummary(data),
    "demand-only": isSeasonalArticleSummary(data) && !data.price,
    sold: isSaleArticleSummary(data) && data.sold,
    discounted: isRentalArticleSummary(data) && isDiscountedPrice(data.bookingSuggestion?.price)
  });

  return (
    <article className={wrapperClasses}>
      <LocaleLink to={target}>

        <div ref={sliderRef}
             className="slider" style={{ aspectRatio: view === "map" ? 16/9 : 5/6}}>
          {data.images.length > 0 &&
            <>
              <div ref={sliderTrackRef}
                   className="slider-track"
                   onScrollCapture={onSliderScroll}>
                {data.images.map((image, i) => (
                  <RatioImage key={i}
                              className="slider-item"
                              images={image.assets}
                              alt={data.title.value}
                              ratio={ratio} lazy={view !== "map"} />)
                )}
              </div>
              <button className={classNames("slider-arrow", "left", { mask: scrollIndex === 0 })}
                      onClick={e => onSliderNav(e, -1)}>
                <IconChevronLeft/>
              </button>
              <button className={classNames("slider-arrow", "right", { mask: scrollIndex + 1 === scrollLength })}
                      onClick={e => onSliderNav(e, 1)}>
                <IconChevronRight/>
              </button>
            </>
          }
        </div>

        <div className="resume">

          {data.images.length > 0 &&
            <div ref={bulletsRef}
                 className="slider-bullets">
              <ul ref={bulletTrackRef}
                  className="slider-bullets-track">
                {data.images.map((_, i) =>
                  <li key={i}
                      {...(i === 0) && {ref: bulletRef}}
                      className={classNames("slider-bullets-item", {active: i === scrollIndex})}/>
                )}
              </ul>
            </div>
          }

          {(isRentalArticleSummary(data) && !!data.bookingSuggestion) &&
            <div className={suggestionClasses}>
              <IconCalendar/>
              {print.period(data.bookingSuggestion.period)}
              &nbsp;&nbsp;|&nbsp;&nbsp;
              {data.bookingSuggestion.price === "onDemand"
                ? intl.formatMessage(bookingTranslations.priceOnDemand)
                : print.price(data.bookingSuggestion.price.amount, {trailingZeroDisplay: "stripIfInteger"})
              }
            </div>
          }

          {isSeasonalArticleSummary(data) &&
            <div className={suggestionClasses}>
              <IconCalendar/>
              {!data.price && intl.formatMessage(bookingTranslations.onDemandOnly)}
              {!!data.price &&
                <>
                  {print.period(data.price.period as GQLPeriod, true)}
                  <IconCoins/>
                  {data.price.isMonthly
                    ? intl.formatMessage(bookingTranslations.pricePerMonth, {price: print.price(data.price.value, {trailingZeroDisplay: "stripIfInteger"})})
                    : print.price(data.price!.value, {trailingZeroDisplay: "stripIfInteger"})}
                </>
              }
            </div>
          }

          {isAnnualArticleSummary(data) &&
            <div className={suggestionClasses}>
              <IconCalendar/>
              {!data.price && intl.formatMessage(bookingTranslations.onDemandOnly)}
              {!!data.price &&
                <>
                  {intl.formatMessage(bookingTranslations.availableFromDateShort, {
                    date: intl.formatDate(castDate(data.price.availableAt).toDate(), {
                      day: "numeric",
                      month: "long",
                      year: "numeric"
                    })
                  })}
                  <IconCoins/>
                  {data.price.onDemand
                    ? intl.formatMessage(bookingTranslations.onDemandOnly)
                    : intl.formatMessage(bookingTranslations.pricePerMonth, {
                      price: print.price(data.price.value, { trailingZeroDisplay: "stripIfInteger" })
                    })
                  }
                </>
              }
            </div>
          }

          {isSaleArticleSummary(data) &&
            <div className={suggestionClasses}>
              <IconCoins />
              {!data.price || data.price.onDemand
                ? intl.formatMessage(bookingTranslations.onDemandOnly)
                : print.price(data.price.value, { trailingZeroDisplay: "stripIfInteger" })
              }
            </div>
          }

          <div className="title-features">
            <h1>{data.title.value}</h1>
            {!isMapView && <ResumeFeatures {...data.features} />}
          </div>

        </div>

        {(!isMapView && !isRentalArticleSummary(data)) &&
          <TagList data={data} />
        }

        {(!isMapView && isRentalArticleSummary(data) && !!data.discounts && data.discounts.length > 0) &&
          <DisplayDiscount data={data.discounts}
                           fixed={fixedDiscountView}
                           onClick={onDiscountClick} />
        }

      </LocaleLink>
    </article>);

}

export default Summary;
